"use client"

import { pageDropAtom } from "@/app/[blockchain]/collections/[id]/drops/page-drop.reatom"
import { DropFee } from "@/app/collections/drops/view/drop-fee"
import {
  OrderWidgetLayout,
  OrderWidgetSkeleton,
} from "@/app/item/view/item-order-widget"
import { PriceInUsd } from "@/app/price"
import { PriceInCrypto } from "@/app/price"
import type { DropPhase } from "@anaconda/core/drop/types"
import { DropPhaseType } from "@anaconda/core/drop/types"
import { UNKNOWN_CURRENCY } from "@anaconda/core/price/types"
import { Trans, useLingui } from "@lingui/react/macro"
import { Card, Skeleton } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { ErrorBoundary } from "react-error-boundary"
import { DropAction } from "../actions/drop-action"
import { DropMoleculeReatom } from "../store/drop-store.reatom"
import { MintInfo } from "./mint-info"
import { MintStatus } from "./mint-status"
import { ProgressBar } from "./progress-bar"

export const DropOrderWidget = reatomComponent(() => {
  return (
    <ErrorBoundary fallback={<DropOrderWidgetSkeleton />}>
      <DropOrderWidgetInner />
    </ErrorBoundary>
  )
}, "DropOrderWidget")

const DropOrderWidgetInner = reatomComponent(() => {
  const { t } = useLingui()
  const drop = pageDropAtom()

  const {
    isLimitedEditionAtom,
    mintedCountAtom,
    hasMultiplePhasesAtom,
    phasesAtom,
    myMintsAtom,
    unfoldedPhaseIndexAtom,
    dropDisplayPriceAtom,
    dropFeeAtom,
  } = useMolecule(DropMoleculeReatom)

  const isLimitedEdition = isLimitedEditionAtom()
  const mintedCount = mintedCountAtom()
  const userMintedCount = myMintsAtom()

  const phases = phasesAtom()
  const hasMultiplePhases = hasMultiplePhasesAtom()
  const unfoldedPhaseIndex = unfoldedPhaseIndexAtom()

  const displayPrice = dropDisplayPriceAtom()
  const fee = dropFeeAtom()

  if (!drop) {
    return <DropOrderWidgetSkeleton />
  }

  const getWidgetTitle = (phaseType: DropPhaseType) => {
    if (!hasMultiplePhases) return t`Mint price`

    switch (phaseType) {
      case DropPhaseType.Allowlist:
        return t`Allowlist`
      case DropPhaseType.Public:
        return t`Public`
      default:
        return t`Public`
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {isLimitedEdition && hasMultiplePhases && (
        <Card className="flex flex-col gap-4 p-4">
          <ProgressBar
            drop={drop}
            mintedCount={mintedCount}
            withHeader
            userMintedCount={userMintedCount}
          />
        </Card>
      )}
      {phases.map((phase: DropPhase, index: number) => (
        <OrderWidgetLayout
          key={`${phase.startDate}-${index}`}
          isFolded={hasMultiplePhases && index !== unfoldedPhaseIndex}
          header={
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                {phase.name && (
                  <span className="text-foreground/80">{phase.name}</span>
                )}
                <span className="flex items-center justify-center gap-2.5 rounded bg-foreground/10 py-1 px-2 text-xs text-foreground/60">
                  {getWidgetTitle(phase.type)}
                </span>
              </div>
              <MintStatus phaseNumber={index} />
            </div>
          }
          price={
            <div className="flex items-baseline gap-3">
              <PriceInUsd
                className=" font-headline text-2xl"
                {...displayPrice}
              />
              <PriceInCrypto
                className="[&&]:text-base"
                displayZeroAsFree
                {...displayPrice}
              />
            </div>
          }
          priceInfo={
            <MintInfo
              drop={drop}
              phaseType={phase.type}
              maxMintPerWallet={phase.maxMintPerWallet}
            />
          }
          primaryAction={
            <div className="flex w-full flex-col items-center gap-4">
              <DropAction
                phaseNumber={index}
                drop={drop}
                className="w-full [&_.input-number]:w-32 [&_.mint-button]:w-full"
              />
              {fee.amount !== "0" &&
                fee.currency.abbreviation !== UNKNOWN_CURRENCY.abbreviation && (
                  <span className="text-foreground/60 text-xs">
                    <Trans>
                      Mint fee is <DropFee /> per item
                    </Trans>
                  </span>
                )}
            </div>
          }
          secondaryAction={null}
        />
      ))}
    </div>
  )
}, "DropOrderWidgetInner")

export function DropOrderWidgetSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-1 p-4">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-full" />
      </Card>
      <OrderWidgetSkeleton />
    </div>
  )
}
